import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { BOOKINGS_QUERY_LIMIT } from '@core/catalog/catalogConstants';
import {
  CHECKOUT_FETCH_POLICY,
  CHECKOUT_QUERY_OPTIONS,
} from '@core/api/apolloQueryOptions';
import {
  groupBookingsByCheckout,
  findCheckoutGroupByPaymentId,
  type CheckoutOrderGroup,
} from '@core/commerce/orders';
import { useStatusModal } from '@shared/context/StatusModalContext';
import { useAuth } from '@features/auth/context/AuthContext';
import { useQuery, useMutation } from '@apollo/client/react';
import { getDefaultGraphqlClient } from '@core/api/graphqlClient';
import { apolloContextForShop } from '@core/api/apolloContext';
import { MULTI_SHOP_ENABLED, getEnvShopId } from '@core/config/shops';
import { useApolloClients } from '@core/context/ApolloClientsContext';
import { useShopIdsKey } from '@core/tenant/useShopIdsKey';
import {
  cartStockHash,
  readCartStockSnapshot,
  writeCartStockSnapshot,
} from './cartStockCache';
import {
  GET_USER_BOOKINGS,
  GET_PRODUCTS_STOCK,
  INSERT_BOOKING,
  INSERT_BOOKING_WITH_STOCK_UPDATE,
  UPDATE_BOOKING_PAYMENT,
  GET_USER_ADDRESSES,
  INSERT_USER_ADDRESS,
} from '../graphql/queries';
import {
  GraphQLBooking,
  GraphQLBookedProduct,
  GetProductsStockResponse,
  GetUserBookingsResponse,
  InsertBookingResponse,
  InsertBookingWithStockResponse,
} from '../graphql/types';
import { CartItem, Address } from '../types';
import {
  resolveLineShopId,
  resolveLocalProductId,
} from '@core/utils/cartLineUtils';

export interface Booking {
  id: string;
  accepted_at?: string | null;
  address?: string | null;
  bill_products?: CartItem[];
  cancel_reason?: string | null;
  cancelled_at?: string | null;
  commissionAmount?: string | number | null; // Can be string or number
  coupon_code?: string | null;
  coupon_used?: boolean | null;
  created_at: string;
  date?: string | null;
  customer?: string | null;
  delivery?: string | null;
  dispatched_at?: string | null;
  delivered_at?: string | null;
  inTransit_at?: string | null;
  is_online?: boolean | null;
  netAmount?: string | number | null; // Can be string or number
  pay_type?: string | null;
  payment_id?: string | null;
  payment_type?: string | null;
  payment_verify?: boolean | null;
  refund_reason?: string | null;
  refund_status?: string | null;
  rejected_at?: string | null;
  shop_id: string;
  status?: string | null;
  tax?: string | number | null; // Can be string or number
  time?: string | null;
  tracking_image?: string | null;
  tracking_id?: string | null;
  total: string | number;
  updated_at: string;
  user_id: string;
  userAddress?: {
    id: string;
    name: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
    landmark?: string | null;
    mobile: string;
  } | null;
  mst_booked_products?: GraphQLBookedProduct[];
}

/** Result shape for callers that must handle multi-shop partial placement after payment. */
export interface CreateBookingResult {
  bookings: Booking[];
  /** Seller `shop_id` values that did not produce a booking on this invocation. */
  failedShopIds: string[];
}

/** Options for checkout when React auth state may not yet hold per-shop `mst_users` rows (e.g. payment return). */
export interface CreateBookingOptions {
  userIdByShop?: Record<string, string>;
  /**
   * PSP already captured — skip standalone `validateCartStock` which races tenant hydrate and rejects valid paid checkouts.
   * Stock is still enforced via `INSERT_BOOKING_WITH_STOCK_UPDATE` when `paymentVerify` is true.
   */
  recoveryAfterCapturedPayment?: boolean;
}

/**
 * Map GraphQL booking to app Booking type
 * Keep the same field names as GraphQL for consistency
 */
const mapGraphQLBookingToBooking = (gqlBooking: GraphQLBooking): Booking => {
  return {
    id: gqlBooking.id,
    accepted_at: gqlBooking.accepted_at,
    address: gqlBooking.address,
    bill_products: gqlBooking.bill_products as CartItem[],
    cancel_reason: gqlBooking.cancel_reason,
    cancelled_at: gqlBooking.cancelled_at,
    commissionAmount: gqlBooking.commissionAmount,
    coupon_code: gqlBooking.coupon_code,
    coupon_used: gqlBooking.coupon_used,
    created_at: gqlBooking.created_at,
    date: gqlBooking.date,
    customer: gqlBooking.customer,
    delivery: gqlBooking.delivery,
    dispatched_at: gqlBooking.dispatched_at,
    delivered_at: gqlBooking.delivered_at,
    inTransit_at: gqlBooking.inTransit_at,
    is_online: gqlBooking.is_online,
    netAmount: gqlBooking.netAmount,
    pay_type: gqlBooking.pay_type,
    payment_id: gqlBooking.payment_id,
    payment_type: gqlBooking.payment_type,
    payment_verify: gqlBooking.payment_verify,
    refund_reason: gqlBooking.refund_reason,
    refund_status: gqlBooking.refund_status,
    rejected_at: gqlBooking.rejected_at,
    shop_id: gqlBooking.shop_id,
    status: gqlBooking.status,
    tax: gqlBooking.tax,
    time: gqlBooking.time,
    tracking_image: gqlBooking.tracking_image,
    tracking_id: gqlBooking.tracking_id,
    total: gqlBooking.total,
    updated_at: gqlBooking.updated_at,
    user_id: gqlBooking.user_id,
    userAddress: gqlBooking.userAddress || null,
    mst_booked_products: gqlBooking.mst_booked_products || [],
  };
};

const getEffectiveStock = (
  stocks?: number | null,
  quantity?: number | null
) => {
  const stockValue = Number(stocks ?? 0);
  const quantityValue = Number(quantity ?? 0);
  return Math.max(stockValue, quantityValue, 0);
};

const normalizeText = (value?: string) => (value || '').trim().toLowerCase();

const getVariantFieldValue = (
  variant: Record<string, unknown>,
  matcher: (key: string) => boolean
) => {
  for (const [key, value] of Object.entries(variant)) {
    if (matcher(key) && value != null) {
      return String(value);
    }
  }
  return '';
};

const isColorKey = (key: string) => key.toLowerCase().includes('color');
const isSizeKey = (key: string) => key.toLowerCase().includes('size');

const variantMatchesCartItem = (
  variant: Record<string, unknown>,
  item: CartItem
) => {
  const itemColor = normalizeText(item.color);
  const itemSize = normalizeText(item.size);
  const variantColor = normalizeText(getVariantFieldValue(variant, isColorKey));
  const variantSize = normalizeText(getVariantFieldValue(variant, isSizeKey));

  const colorMatches = !itemColor || itemColor === variantColor;
  const sizeMatches = !itemSize || itemSize === variantSize;
  return colorMatches && sizeMatches;
};

const getVariantStock = (variant: Record<string, unknown>) =>
  Number(variant.stocks ?? variant.quantity ?? 0);

const getProductVariants = (attributes: unknown): Record<string, unknown>[] => {
  if (!attributes) return [];
  if (Array.isArray(attributes)) return attributes as Record<string, unknown>[];
  if (typeof attributes === 'string') {
    try {
      const parsed = JSON.parse(attributes);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

function groupCartByShop(items: CartItem[]): Map<string, CartItem[]> {
  const m = new Map<string, CartItem[]>();
  for (const it of items) {
    const k = resolveLineShopId(it);
    if (!m.has(k)) m.set(k, []);
    m.get(k)!.push(it);
  }
  return m;
}

async function resolveAddressIdForShop(
  snapshot: Address,
  userId: string,
  shopId: string
): Promise<string | null> {
  const client = getDefaultGraphqlClient();
  const { data } = await client.query<{
    mst_user_address?: { id: string; pincode?: string; address?: string }[];
  }>({
    query: GET_USER_ADDRESSES,
    variables: { userId, shopId },
    fetchPolicy: CHECKOUT_FETCH_POLICY,
    context: apolloContextForShop(shopId),
  });
  const list = data?.mst_user_address ?? [];
  const match = list.find(
    (a: { pincode?: string; address?: string }) =>
      a.pincode === snapshot.zipCode && a.address === snapshot.street
  );
  if (match) return match.id as string;
  const res = await client.mutate({
    mutation: INSERT_USER_ADDRESS,
    variables: {
      address: {
        name: snapshot.fullName,
        address: snapshot.street,
        city: snapshot.city,
        state: snapshot.state,
        pincode: snapshot.zipCode,
        mobile: snapshot.phone,
        save_address_as: 'Home',
        is_default: list.length === 0,
        shop_id: shopId,
        user: userId,
      },
    },
    context: apolloContextForShop(shopId),
  });
  const inserted = res.data as { insert_mst_user_address_one?: { id: string } };
  return inserted?.insert_mst_user_address_one?.id ?? null;
}

export type BookingsContextValue = ReturnType<typeof useBookingsInternal>;

const BookingsContext = createContext<BookingsContextValue | null>(null);

export function BookingsProvider({ children }: { children: ReactNode }) {
  const value = useBookingsInternal();
  return (
    <BookingsContext.Provider value={value}>{children}</BookingsContext.Provider>
  );
}

export const useBookings = (): BookingsContextValue => {
  const ctx = useContext(BookingsContext);
  if (!ctx) {
    throw new Error('useBookings must be used within BookingsProvider');
  }
  return ctx;
};

export function useBookingsOptional(): BookingsContextValue | null {
  return useContext(BookingsContext);
}

function useBookingsInternal() {
  const { userData, getUserForShop } = useAuth();
  const { showError } = useStatusModal();
  const { shops, tenantLoading } = useApolloClients();
  const shopIdsKey = useShopIdsKey();
  const defaultShop = shops[0];

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const primaryUserId =
    (defaultShop ? getUserForShop(defaultShop.id)?.id : undefined) ??
    userData?.id ??
    undefined;

  const bookingShopId = defaultShop?.id ?? getEnvShopId();
  const {
    data,
    loading: queryLoading,
    refetch,
  } = useQuery<GetUserBookingsResponse>(GET_USER_BOOKINGS, {
    client: getDefaultGraphqlClient(),
    variables: {
      userId: primaryUserId,
      shopId: defaultShop?.id,
      limit: BOOKINGS_QUERY_LIMIT,
    },
    skip: MULTI_SHOP_ENABLED || !primaryUserId || !defaultShop?.id,
    ...CHECKOUT_QUERY_OPTIONS,
    context: apolloContextForShop(bookingShopId),
  });

  const [updateBookingPayment] = useMutation(UPDATE_BOOKING_PAYMENT, {
    client: getDefaultGraphqlClient(),
    context: apolloContextForShop(bookingShopId),
  });

  const shopUserIdsKey = useMemo(
    () =>
      shops
        .map(s => `${s.id}:${getUserForShop(s.id)?.id ?? ''}`)
        .sort()
        .join('|'),
    [shops, getUserForShop, userData?.id]
  );

  const loadMultiShopBookings = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const all: Booking[] = [];
      await Promise.all(
        shops.map(async s => {
          const uid = getUserForShop(s.id)?.id;
          if (!uid) {
            return;
          }
          try {
            const client = getDefaultGraphqlClient();
            const { data: d } = await client.query<GetUserBookingsResponse>({
              query: GET_USER_BOOKINGS,
              variables: {
                userId: uid,
                shopId: s.id,
                limit: BOOKINGS_QUERY_LIMIT,
              },
              fetchPolicy: CHECKOUT_FETCH_POLICY,
              context: apolloContextForShop(s.id),
            });
            if (d?.mst_bookings) {
              all.push(...d.mst_bookings.map(mapGraphQLBookingToBooking));
            }
          } catch (e) {
            console.error('[useBookings] fetch orders', e);
          }
        })
      );
      all.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      setBookings(all);
    } finally {
      setLoading(false);
    }
  }, [shops, getUserForShop]);

  const validateCartStock = async (
    cartItems: CartItem[]
  ): Promise<{ ok: boolean; message?: string }> => {
    if (!cartItems.length) {
      return { ok: false, message: 'Your cart is empty.' };
    }

    const groups = groupCartByShop(cartItems);
    const shortages: string[] = [];
    const stockHash = cartStockHash(cartItems);

    await Promise.all(
      [...groups.entries()].map(async ([shopId, items]) => {
      const shopMeta = shops.find(s => s.id === shopId);
      if (!shopMeta) {
        shortages.push(`Unknown shop: ${shopId}`);
        return;
      }
      const client = getDefaultGraphqlClient();
      const productIds = Array.from(
        new Set(items.map(item => resolveLocalProductId(item.productId)))
      );
      if (!productIds.length) {
        shortages.push('Invalid cart items.');
        return;
      }

      let stockRows = readCartStockSnapshot(stockHash, shopMeta.id);
      if (!stockRows) {
        const { data } = await client.query<GetProductsStockResponse>({
          query: GET_PRODUCTS_STOCK,
          variables: { productIds, shopId: shopMeta.id },
          fetchPolicy: CHECKOUT_FETCH_POLICY,
          context: apolloContextForShop(shopMeta.id),
        });
        stockRows = data?.mst_product || [];
        writeCartStockSnapshot(stockHash, shopMeta.id, stockRows);
      }

      const productById = new Map(
        stockRows.map(product => [product.id, product])
      );

      for (const item of items) {
        const localPid = resolveLocalProductId(item.productId);
        const product = productById.get(localPid);
        const productName = product?.name || item.name || 'Selected product';

        if (!product) {
          shortages.push(
            `${productName} (requested ${item.quantity}, available 0)`
          );
          continue;
        }

        const variants = getProductVariants(product.attributes);
        if (variants.length > 0) {
          const matchedVariant = variants.find(variant =>
            variantMatchesCartItem(variant, item)
          );
          const available = matchedVariant
            ? getVariantStock(matchedVariant)
            : 0;
          if (item.quantity > available) {
            const variantLabel =
              item.color || item.size
                ? ` [${[item.color, item.size].filter(Boolean).join(' / ')}]`
                : '';
            shortages.push(
              `${productName}${variantLabel} (requested ${item.quantity}, available ${available})`
            );
          }
        } else {
          const available = getEffectiveStock(product.stocks, product.quantity);
          if (item.quantity > available) {
            shortages.push(
              `${productName} (requested ${item.quantity}, available ${available})`
            );
          }
        }
      }
      })
    );

    if (shortages.length) {
      return {
        ok: false,
        message: `Some items are out of stock or have limited quantity: ${shortages.join(', ')}`,
      };
    }

    return { ok: true };
  };

  useEffect(() => {
    if (MULTI_SHOP_ENABLED) return;
    if (data?.mst_bookings) {
      setBookings(data.mst_bookings.map(mapGraphQLBookingToBooking));
    } else {
      setBookings([]);
    }
    setLoading(queryLoading);
  }, [data, queryLoading]);

  useEffect(() => {
    if (!MULTI_SHOP_ENABLED) return;
    if (tenantLoading || !shops.length) return;
    const hasAnyCustomer = shopUserIdsKey.split('|').some(pair => {
      const [, uid] = pair.split(':');
      return Boolean(uid);
    });
    if (!hasAnyCustomer) {
      setBookings([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    void loadMultiShopBookings().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [
    MULTI_SHOP_ENABLED,
    shopUserIdsKey,
    tenantLoading,
    shopIdsKey,
    loadMultiShopBookings,
  ]);

  const refetchBookings = useCallback(async (): Promise<void> => {
    if (MULTI_SHOP_ENABLED) {
      await loadMultiShopBookings();
      return;
    }
    try {
      const { data: next } = await refetch();
      if (next?.mst_bookings) {
        setBookings(next.mst_bookings.map(mapGraphQLBookingToBooking));
      } else {
        setBookings([]);
      }
    } catch (e) {
      console.error('[useBookings] refetchBookings', e);
    }
  }, [loadMultiShopBookings, refetch]);

  const createBooking = async (
    cartItems: CartItem[],
    addressId: string,
    paymentType: string,
    total: number,
    netAmount: number,
    tax: number,
    commissionAmount?: number,
    paymentId?: string,
    couponCode?: string,
    couponUsed?: boolean,
    delivery?: number | string,
    time?: string,
    paymentVerify?: boolean,
    addressSnapshot?: Address | null,
    options?: CreateBookingOptions
  ): Promise<CreateBookingResult | null> => {
    if (
      tenantLoading &&
      shops.length === 0 &&
      !options?.recoveryAfterCapturedPayment
    ) {
      showError(
        'Still loading',
        'Store list is still loading — please wait a moment and try again.'
      );
      return null;
    }

    if (!cartItems.length) {
      showError('Cart Empty', 'Your cart is empty.');
      return null;
    }

    if (options?.recoveryAfterCapturedPayment && shops.length === 0) {
      console.warn('[useBookings] recovery path: tenant shops missing', {
        tenantLoading,
        shopCount: shops.length,
      });
      showError(
        'Stores not ready',
        'Please refresh this page in a moment to finish placing your paid order.'
      );
      return null;
    }

    if (!options?.recoveryAfterCapturedPayment) {
      const stockValidation = await validateCartStock(cartItems);
      if (!stockValidation.ok) {
        showError(
          'Stock Unavailable',
          stockValidation.message || 'Stock check failed'
        );
        return null;
      }
    }

    const cartMoneySubtotal = cartItems.reduce(
      (acc, item) => acc + item.price * item.quantity,
      0
    );

    const deliveryBase =
      delivery === undefined || delivery === null
        ? null
        : typeof delivery === 'string'
          ? parseFloat(delivery) || null
          : delivery;

    const client = getDefaultGraphqlClient();

    const submitBookingInsert = async (
      items: CartItem[],
      shopMeta: { id: string },
      monetary: {
        bookingTotal: number;
        bookingNet: number;
        bookingTax: number;
        bookingCommission: number;
        bookingDelivery: number | null;
      },
      uid: string,
      resolvedAddressId: string
    ): Promise<Booking | null> => {
      try {
        const billProducts = items.map(item => ({
          productId: item.productId,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          color: item.color,
          size: item.size,
          imageUrl: item.imageUrl,
        }));

        const bookedProducts = items.map(item => {
          const itemTotal = item.price * item.quantity;
          const attributes: Record<string, string> = {};

          if (item.color) attributes.color = item.color;
          if (item.size) attributes.size = item.size;
          if (item.imageUrl) attributes.imageUrl = item.imageUrl;

          const localPid = resolveLocalProductId(item.productId);

          return {
            product: localPid,
            quantity: item.quantity,
            price: item.price.toString(),
            paid_price: item.price.toString(),
            coupon_price: '0',
            saved_price: '0',
            sub_total: itemTotal.toString(),
            attributes: Object.keys(attributes).length > 0 ? attributes : null,
            status: 'Pending',
            return_status: null,
            is_review: false,
            review_rating: null,
            star_rating: null,
            review_photos_videos: null,
            delivered_at: null,
            shop_id: shopMeta.id,
          };
        });

        const now = new Date();
        const dateStr = now.toISOString().split('T')[0];
        const timeStr =
          time || now.toTimeString().split(' ')[0].substring(0, 5);

        const bookingInput = {
          user_id: uid,
          shop_id: shopMeta.id,
          customer: uid,
          date: dateStr,
          time: timeStr,
          status: 'Pending',
          total: monetary.bookingTotal.toString(),
          netAmount: monetary.bookingNet.toString(),
          tax: monetary.bookingTax.toString(),
          commissionAmount: monetary.bookingCommission.toString(),
          payment_type: paymentType,
          payment_id: paymentId || null,
          payment_verify: paymentVerify || false,
          pay_type: 'website',
          is_online: paymentType !== 'cash',
          coupon_code: couponCode || null,
          coupon_used: couponUsed || false,
          address: resolvedAddressId,
          bill_products: billProducts,
          delivery: monetary.bookingDelivery,
          mst_booked_products: {
            data: bookedProducts,
          },
          accepted_at: null,
          dispatched_at: null,
          inTransit_at: null,
          delivered_at: null,
          cancelled_at: null,
          rejected_at: null,
          cancel_reason: null,
          refund_status: null,
          refund_reason: null,
          tracking_id: null,
          tracking_image: null,
        };

        let resultData:
          | InsertBookingResponse['insert_mst_bookings_one']
          | InsertBookingWithStockResponse['insert_mst_bookings_one']
          | null
          | undefined;

        if (paymentVerify) {
          const productIds = Array.from(
            new Set(items.map(item => resolveLocalProductId(item.productId)))
          );

          const verifyHash = cartStockHash(items);
          let stockRows = readCartStockSnapshot(verifyHash, shopMeta.id);
          if (!stockRows) {
            const stockSnapshot = await client.query<GetProductsStockResponse>({
              query: GET_PRODUCTS_STOCK,
              variables: { productIds, shopId: shopMeta.id },
              fetchPolicy: CHECKOUT_FETCH_POLICY,
              context: apolloContextForShop(shopMeta.id),
            });
            stockRows = stockSnapshot.data?.mst_product || [];
            writeCartStockSnapshot(verifyHash, shopMeta.id, stockRows);
          }
          const productById = new Map(
            stockRows.map(product => [product.id, product])
          );

          const productItemsMap = items.reduce<Record<string, CartItem[]>>(
            (acc, item) => {
              const lp = resolveLocalProductId(item.productId);
              if (!acc[lp]) acc[lp] = [];
              acc[lp].push(item);
              return acc;
            },
            {}
          );

          const stockUpdates = Object.entries(productItemsMap).map(
            ([productId, cartLines]) => {
              const product = productById.get(productId);
              const variants = getProductVariants(product?.attributes);

              if (variants.length > 0) {
                const nextVariants = variants.map(variant => ({ ...variant }));

                for (const item of cartLines) {
                  const variantIndex = nextVariants.findIndex(variant =>
                    variantMatchesCartItem(variant, item)
                  );

                  if (variantIndex === -1) continue;

                  const variant = nextVariants[variantIndex];
                  const currentVariantStock = getVariantStock(variant);
                  const nextVariantStock = Math.max(
                    0,
                    currentVariantStock - item.quantity
                  );
                  variant.stocks = nextVariantStock;
                  variant.quantity = nextVariantStock;
                }

                const totalVariantStock = nextVariants.reduce(
                  (sum, variant) => sum + getVariantStock(variant),
                  0
                );

                return {
                  where: {
                    id: { _eq: productId },
                    shop_id: { _eq: shopMeta.id },
                    isdeleted: { _eq: false },
                  },
                  _set: {
                    attributes: nextVariants,
                    stocks: totalVariantStock,
                    quantity: totalVariantStock,
                  },
                };
              }

              const qty = cartLines.reduce(
                (sum, item) => sum + item.quantity,
                0
              );
              const currentStock = getEffectiveStock(
                product?.stocks,
                product?.quantity
              );
              const nextStock = Math.max(0, currentStock - qty);
              return {
                where: {
                  id: { _eq: productId },
                  shop_id: { _eq: shopMeta.id },
                  isdeleted: { _eq: false },
                },
                _set: {
                  stocks: nextStock,
                  quantity: nextStock,
                },
              };
            }
          );

          const result = await client.mutate<InsertBookingWithStockResponse>({
            mutation: INSERT_BOOKING_WITH_STOCK_UPDATE,
            variables: {
              booking: bookingInput,
              stockUpdates,
            },
            context: apolloContextForShop(shopMeta.id),
          });

          const affectedRows =
            result.data?.update_mst_product_many?.length || 0;
          if (affectedRows < Object.keys(productItemsMap).length) {
            showError(
              'Stock Update Failed',
              'Order was created, but stock could not be updated for one or more items. Please contact support.'
            );
          }

          resultData = result.data?.insert_mst_bookings_one;
        } else {
          const result = await client.mutate<InsertBookingResponse>({
            mutation: INSERT_BOOKING,
            variables: {
              booking: bookingInput,
            },
            context: apolloContextForShop(shopMeta.id),
          });
          resultData = result.data?.insert_mst_bookings_one;
        }

        if (resultData) {
          return mapGraphQLBookingToBooking(resultData);
        }
        return null;
      } catch (err) {
        const msgErr = err as Error;
        console.error('Error creating booking:', msgErr);
        showError(
          'Booking Error',
          msgErr.message || 'Failed to create booking'
        );
        return null;
      }
    };

    try {
      setError(null);

      const groups = groupCartByShop(cartItems);
      const multiSplit = MULTI_SHOP_ENABLED && groups.size > 1;

      const runSingleCheckoutSlice = async (
        items: CartItem[],
        monetary: {
          bookingTotal: number;
          bookingNet: number;
          bookingTax: number;
          bookingCommission: number;
          bookingDelivery: number | null;
        },
        shopMeta: { id: string },
        shopKey: string
      ): Promise<Booking | null> => {
        const uid =
          options?.userIdByShop?.[shopKey] ??
          (MULTI_SHOP_ENABLED ? getUserForShop(shopKey)?.id : undefined) ??
          userData?.id;

        if (!uid) {
          showError(
            'Authentication Error',
            'User not logged in for one or more stores.'
          );
          return null;
        }

        let resolvedAddressId = addressId;
        if (MULTI_SHOP_ENABLED && addressSnapshot) {
          const rid = await resolveAddressIdForShop(
            addressSnapshot,
            uid,
            shopMeta.id
          );
          if (rid) resolvedAddressId = rid;
        }

        return submitBookingInsert(
          items,
          shopMeta,
          monetary,
          uid,
          resolvedAddressId
        );
      };

      if (!multiSplit) {
        const items = cartItems;
        const checkoutShopId = resolveLineShopId(items[0]);
        const shopMeta =
          shops.find(s => s.id === checkoutShopId) ?? defaultShop;

        if (!shopMeta?.id) {
          showError(
            'Checkout Error',
            'Could not determine store for this order.'
          );
          return null;
        }

        const monetary = {
          bookingTotal: Math.round(total),
          bookingNet: Math.round(netAmount ?? total - (tax || 0)),
          bookingTax: Math.round(tax || 0),
          bookingCommission: Math.round(commissionAmount || 0),
          bookingDelivery:
            deliveryBase == null ? null : Math.round(deliveryBase),
        };

        const booking = await runSingleCheckoutSlice(
          items,
          monetary,
          shopMeta,
          checkoutShopId
        );
        return booking ? { bookings: [booking], failedShopIds: [] } : null;
      }

      const nGroups = groups.size;
      const sliceResults = await Promise.all(
        [...groups.entries()].map(async ([shopKey, items]) => {
          const shopMeta = shops.find(s => s.id === shopKey);
          if (!shopMeta?.id) {
            return { fatal: true as const, shopKey, booking: null as Booking | null };
          }

          const shopMoney = items.reduce(
            (acc, item) => acc + item.price * item.quantity,
            0
          );
          const frac =
            cartMoneySubtotal > 0
              ? shopMoney / cartMoneySubtotal
              : 1 / Math.max(1, nGroups);

          const monetary = {
            bookingTotal: Math.round(shopMoney),
            bookingNet: Math.round((netAmount || 0) * frac),
            bookingTax: Math.round((tax || 0) * frac),
            bookingCommission: Math.round((commissionAmount || 0) * frac),
            bookingDelivery:
              deliveryBase != null ? Math.round(deliveryBase * frac) : null,
          };

          const booking = await runSingleCheckoutSlice(
            items,
            monetary,
            shopMeta,
            shopKey
          );
          return { fatal: false as const, shopKey, booking };
        })
      );

      const fatal = sliceResults.find(r => r.fatal);
      if (fatal) {
        showError('Checkout Error', `Unknown store ${fatal.shopKey}.`);
        return null;
      }

      const results: Booking[] = [];
      const failedShopIds: string[] = [];
      for (const r of sliceResults) {
        if (!r.booking) {
          failedShopIds.push(r.shopKey);
          continue;
        }
        results.push(r.booking);
      }

      if (results.length === 0) {
        return null;
      }
      return { bookings: results, failedShopIds };
    } finally {
      setLoading(false);
    }
  };

  const shopNamesById = useMemo(
    () => Object.fromEntries(shops.map(s => [s.id, s.name])),
    [shops]
  );

  const checkoutGroups: CheckoutOrderGroup[] = useMemo(
    () => groupBookingsByCheckout(bookings, shopNamesById),
    [bookings, shopNamesById]
  );

  const getCheckoutGroupByPaymentId = useCallback(
    (paymentId: string | null | undefined): CheckoutOrderGroup | undefined =>
      findCheckoutGroupByPaymentId(checkoutGroups, paymentId),
    [checkoutGroups]
  );

  return {
    bookings,
    checkoutGroups,
    getCheckoutGroupByPaymentId,
    loading,
    error,
    createBooking,
    validateCartStock,
    updateBookingPayment,
    refetch,
    refetchBookings,
  };
};
